import {
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon
} from '@mui/icons-material';
import {
  Button,
  Checkbox,
  Chip,
  FormControl,
  Grid,
  Paper,
  TextField
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import {
  CounterpartyFilters,
  CounterpartyQueryFilters,
  CounterpartyType
} from 'openapi';

import { useTranslations } from 'context/TranslationContext';

import { CounterpartyFilterFormValues } from 'utils/constants/counterparty';
import {
  getCounterpartyFilter,
  toCounterpartyQueryFilters
} from 'utils/helpers/counterPartyHelper';

interface CounterPartiesFilterPanelProps {
  filtersData?: CounterpartyFilters;
  areFiltersVisible?: boolean;
  isNotApprovedView?: boolean;
  handleApplyFilters: (data: CounterpartyQueryFilters) => void;
}

export const CounterPartiesFilterPanel = ({
  filtersData,
  areFiltersVisible,
  isNotApprovedView = false,
  handleApplyFilters
}: CounterPartiesFilterPanelProps) => {
  const filterConfig = getCounterpartyFilter();
  const filterFields = filterConfig.fields;

  const { control, handleSubmit, reset, setValue, watch } =
    useForm<CounterpartyFilterFormValues>({
      defaultValues: filterConfig.defaultValues
    });

  const { translate } = useTranslations();

  const [counterPartyOptions, setCounterPartyOptions] = useState(
    filtersData || {}
  );

  const selectedType = watch('type');
  const isReimbursementSelected =
    selectedType === CounterpartyType.REIMBURSEMENT;

  useEffect(() => {
    setCounterPartyOptions(filtersData || {});
    reset();
  }, [filtersData, reset]);

  useEffect(() => {
    if (isReimbursementSelected) {
      setValue('expenseTypes', []);
    }
  }, [isReimbursementSelected, setValue]);

  return (
    <Paper
      elevation={4}
      className={`filters-container ${!areFiltersVisible && 'hidden'}`}
    >
      <Grid container>
        <form
          className={`filters ${!areFiltersVisible && 'hidden'}`}
          onSubmit={handleSubmit((values) =>
            handleApplyFilters(toCounterpartyQueryFilters(values))
          )}
        >
          <Grid sx={{ overflow: 'auto' }} container spacing={1}>
            {filterFields
              .filter(
                (filter) => !(filter.name === 'approved' && isNotApprovedView)
              )
              .map((filter) => (
                <Grid key={filter.name} size={3}>
                  {filter.kind === 'multi' ? (
                    <Controller
                      name={filter.name}
                      control={control}
                      render={({ field }) => (
                        <FormControl
                          sx={{ width: '100%' }}
                          size="small"
                          fullWidth
                        >
                          <Autocomplete
                            {...field}
                            multiple
                            size="small"
                            options={counterPartyOptions[filter.name] || []}
                            disabled={
                              filter.name === 'expenseTypes' &&
                              isReimbursementSelected
                            }
                            disableCloseOnSelect
                            getOptionLabel={(option) => option || ''}
                            onChange={(e, value) => {
                              field.onChange(value);
                              setCounterPartyOptions((previousValue) => {
                                const filteredValues = previousValue[
                                  filter.name
                                ]?.filter(
                                  (item) => !value.some((v) => v === item)
                                );
                                return {
                                  ...previousValue,
                                  [filter.name]: [
                                    ...value,
                                    ...(filteredValues || [])
                                  ]
                                };
                              });
                            }}
                            renderOption={(props, option, { selected }) => (
                              <li {...props}>
                                <Checkbox
                                  icon={
                                    <CheckBoxOutlineBlankIcon fontSize="small" />
                                  }
                                  checkedIcon={
                                    <CheckBoxIcon fontSize="small" />
                                  }
                                  style={{ marginRight: 8 }}
                                  checked={selected}
                                />
                                {option}
                              </li>
                            )}
                            renderValue={(value, getItemProps) =>
                              value
                                .slice(0, 1)
                                .map((option, index) => (
                                  <Chip
                                    sx={{ width: '65%' }}
                                    label={option}
                                    {...getItemProps({ index })}
                                    disabled={index > 1}
                                    size="small"
                                  />
                                ))
                                .concat(
                                  value.length > 1
                                    ? [
                                        <Chip
                                          size="small"
                                          key="more"
                                          label={`+${
                                            value.length - 1
                                          } ${translate('labels.more')}`}
                                        />
                                      ]
                                    : []
                                )
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={translate(filter.translationKey)}
                                placeholder={translate(filter.translationKey)}
                              />
                            )}
                          />
                        </FormControl>
                      )}
                    />
                  ) : (
                    <Controller
                      name={filter.name}
                      control={control}
                      render={({ field }) => {
                        const selectedOption =
                          filter.options.find(
                            (option) => option.value === field.value
                          ) ?? null;

                        return (
                          <FormControl
                            sx={{ width: '100%' }}
                            size="small"
                            fullWidth
                          >
                            <Autocomplete
                              size="small"
                              options={filter.options}
                              value={selectedOption}
                              onBlur={field.onBlur}
                              onChange={(_, option) =>
                                field.onChange(option ? option.value : null)
                              }
                              getOptionLabel={(option) =>
                                translate(option.translationKey)
                              }
                              isOptionEqualToValue={(option, value) =>
                                option.value === value.value
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label={translate(filter.translationKey)}
                                  placeholder={translate(filter.translationKey)}
                                />
                              )}
                            />
                          </FormControl>
                        );
                      }}
                    />
                  )}
                </Grid>
              ))}
          </Grid>

          <Grid container paddingTop={1} gap={2} sx={{ width: '100%' }}>
            <Grid>
              <Button type="submit" variant="contained" size="small">
                {translate('buttons.apply')}
              </Button>
            </Grid>
            <Grid>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  reset();
                  handleApplyFilters(
                    toCounterpartyQueryFilters(filterConfig.defaultValues)
                  );
                }}
              >
                {translate('buttons.reset')}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Grid>
    </Paper>
  );
};
